import {
  initializeFirestore,
  connectFirestoreEmulator,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  serverTimestamp,
  type Firestore,
} from 'firebase/firestore';
import { initializeApp } from 'firebase/app';
import type { User, Organization, OrgMember } from '@gigdriver/domain';
import { fromFirestore } from './serde';
import { callable } from './functions';

let firestore: Firestore;

export function initFirestore() {
  const getEnv = (): Record<string, any> => {
    // Under plain Node/tsx (no Vite), `import.meta` exists but `.env` is
    // `undefined` rather than throwing — an explicit falsy check is required
    // to actually fall through; try/catch around the access never fires.
    const metaEnv = (import.meta as any).env;
    if (metaEnv) return metaEnv;
    return (globalThis as { process?: { env?: Record<string, unknown> } }).process?.env ?? {};
  };

  const env = getEnv();

  const firebaseApp = initializeApp({
    projectId: env.VITE_FIREBASE_PROJECT_ID as string,
    apiKey: env.VITE_FIREBASE_API_KEY as string,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN as string,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET as string,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
    appId: env.VITE_FIREBASE_APP_ID as string,
  });

  firestore = initializeFirestore(firebaseApp, {
    ignoreUndefinedProperties: true,
  });

  try {
    if ((import.meta as any).env.DEV) {
      console.log('Connecting to Firestore emulator...');
      connectFirestoreEmulator(firestore, 'localhost', 8080);
      console.log('✅ Connected to Firestore emulator on localhost:8080');
    }
  } catch (err) {
    console.error('Firestore emulator connection error:', err);
  }

  return firestore;
}

export function getFirestoreInstance(): Firestore {
  if (!firestore) {
    throw new Error('Firestore not initialized. Call initFirestore() first.');
  }
  return firestore;
}

// User operations
export async function getUser(uid: string): Promise<User | null> {
  const docRef = doc(getFirestoreInstance(), 'users', uid);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? fromFirestore<User>(docSnap.data()) : null;
}

export async function createUser(user: Omit<User, 'createdAt' | 'updatedAt'>): Promise<void> {
  const docRef = doc(getFirestoreInstance(), 'users', user.uid);
  await setDoc(docRef, {
    ...user,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function updateUser(uid: string, updates: Partial<User>): Promise<void> {
  const docRef = doc(getFirestoreInstance(), 'users', uid);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// Organization operations
export async function getOrganization(orgId: string): Promise<Organization | null> {
  const docRef = doc(getFirestoreInstance(), 'organizations', orgId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists() ? fromFirestore<Organization>(docSnap.data()) : null;
}

// Single-org bootstrap: the server (createOrganization callable) creates the
// org + the caller's owner membership, and only while no organization exists.
const createOrganizationCallable = callable<{ name: string; timezone?: string }, { orgId: string }>('createOrganization');
// `timezone` is the creator's IANA zone (the browser's); the server validates it
// and stores it as the organization's default so costing never silently falls
// back to UTC.
export async function createOrganization(name: string, timezone?: string): Promise<string> {
  return (await createOrganizationCallable({ name, timezone })).orgId;
}

// Whether the signed-in user may still bootstrap the (single) organization.
export const canCreateOrganization = callable<void, { allowed: boolean }>('canCreateOrganization');

export async function updateOrganization(orgId: string, updates: Partial<Organization>): Promise<void> {
  const docRef = doc(getFirestoreInstance(), 'organizations', orgId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

// Organization member operations (membership is only ever created/changed
// server-side — see members.ts, and the createOrganization / invite callables)
export async function getOrgMember(orgId: string, uid: string): Promise<OrgMember | null> {
  const memberRef = doc(getFirestoreInstance(), 'organizations', orgId, 'members', uid);
  const docSnap = await getDoc(memberRef);
  return docSnap.exists() ? fromFirestore<OrgMember>(docSnap.data()) : null;
}

export async function getOrgMembers(orgId: string): Promise<Record<string, OrgMember>> {
  const membersRef = collection(getFirestoreInstance(), 'organizations', orgId, 'members');
  const snapshot = await getDocs(membersRef);
  const members: Record<string, OrgMember> = {};
  snapshot.forEach(doc => {
    members[doc.id] = fromFirestore<OrgMember>(doc.data());
  });
  return members;
}

// Get every organization the user is a member of (owner or otherwise).
export async function getUserOrganizations(uid: string): Promise<Organization[]> {
  const membershipsQuery = query(
    collectionGroup(getFirestoreInstance(), 'members'),
    where('uid', '==', uid),
  );
  const memberships = await getDocs(membershipsQuery);

  const orgIds = memberships.docs
    .map(m => m.ref.parent.parent?.id)
    .filter((id): id is string => Boolean(id));

  const orgs = await Promise.all(orgIds.map(id => getOrganization(id)));
  return orgs.filter((org): org is Organization => org !== null);
}

export async function logActivity(
  orgId: string,
  entityType: string,
  entityId: string,
  action: string,
  actorId: string,
  changes?: Record<string, any>,
): Promise<void> {
  const activityRef = collection(getFirestoreInstance(), 'organizations', orgId, 'activityLog');
  await setDoc(doc(activityRef), {
    entityType,
    entityId,
    action,
    actorId,
    changes,
    timestamp: serverTimestamp(),
  });
}
